import { groupBy } from "lodash";
import { FoodDto, PurchaseDto } from "../../api";
import { Purchase } from "../../data/purchase";
import { MealWithIntakes } from "../../data/plan";
import { MealIngredient } from "../../data/mealWithIngredients";
import { DishModification, StepIngredientModification } from "../../data/dishModification";
import { emptyNutriValue, NutriValues } from "../../data/nutriValues";
import {
  CookingOverrides,
  createCustomIntakes,
  findById,
  getMIKey,
  getMealKeyAsString,
  IngredientGroup,
  IngredientPair,
  isNullIndex,
  nutriValuesFromIntake,
} from "../../utils/planUtils";
import { formatPurchaseDescription } from "../../utils/purchaseUtils";

export type CookingStep = {
  stepIndex: number | undefined;
  stepText: string | null | undefined;
  ingredientGroups: IngredientGroup[];
};

export type DishGroup = {
  keyString: string;
  description: string;
  meals: MealWithIntakes[];
  steps: CookingStep[];
  allIngredientGroups: IngredientGroup[];
  isNoDishGroup?: boolean;
};

const foodByUnit = (foods: FoodDto[], unitId: number | undefined) =>
  unitId == undefined
    ? undefined
    : foods.find((f) => f.foodUnits.some((fu) => fu.id === unitId));

export function getMIName(
  mi: MealIngredient,
  foods: FoodDto[],
  purchases: PurchaseDto[],
): string {
  if (!isNullIndex(mi.foodUnitFoodId)) {
    return (
      findById(foods, mi.foodUnitFoodId!)?.description ??
      mi.foodUnitFoodDescription ??
      ""
    );
  }
  return (
    formatPurchaseDescription(
      findById(purchases, mi.purchaseUnitPurchaseId!),
    ) ||
    mi.purchaseUnitPurchaseDescription ||
    ""
  );
}

export function buildIngredientGroups(
  pairs: IngredientPair[],
  foods: FoodDto[],
  purchases: PurchaseDto[],
  stepIngredients?: StepIngredientModification[],
): IngredientGroup[] {
  const order: string[] = [];
  const map = new Map<string, IngredientGroup>();
  for (const pair of pairs) {
    const key = getMIKey(pair.mi);
    if (!map.has(key)) {
      order.push(key);
      map.set(key, {
        key,
        pairs: [],
        name: getMIName(pair.mi, foods, purchases),
        // Fallback über `foodUnitId`: synthetische Rezept-Zutaten können
        // zunächst `foodUnitFoodId === -1` haben, solange die Foods noch laden.
        isFoodBased:
          !isNullIndex(pair.mi.foodUnitFoodId) ||
          !isNullIndex(pair.mi.foodUnitId),
      });
    }
    map.get(key)!.pairs.push(pair);
  }

  // Find recipe ingredient
  if (stepIngredients) {
    for (const key of order) {
      const firstMi = map.get(key)!.pairs[0].mi;
      const matchingSi = stepIngredients.find(
        (si) =>
          si.foodUnitId === firstMi.foodUnitId ||
          si.alternatives?.some((alt) => alt.foodUnitId === firstMi.foodUnitId),
      );
      if (matchingSi) {
        map.get(key)!.recipeIngredient = matchingSi;
      }
    }
  }

  return order.map((k) => map.get(k)!);
}

export function buildDishGroups(
  meals: MealWithIntakes[],
  foods: FoodDto[],
  purchases: PurchaseDto[],
  loadedDishes: Record<number, DishModification>,
): DishGroup[] {
  const noDishMeals = meals.filter((m) => isNullIndex(m.meal.dishId));
  const dishMeals = meals.filter((m) => !isNullIndex(m.meal.dishId));

  const groups: DishGroup[] = [];

  if (dishMeals.length > 0) {
    const grouped = groupBy(dishMeals, (m) => getMealKeyAsString(m));
    groups.push(
      ...Object.entries(grouped).map(([keyString, dishMeals]) => {
        return buildSingleDishGroup(
          keyString,
          dishMeals,
          foods,
          purchases,
          loadedDishes,
        );
      }),
    );
  }

  if (noDishMeals.length > 0) {
    const allPairs: IngredientPair[] = noDishMeals.flatMap((meal) =>
      meal.meal.mealIngredients.map((mi) => ({ mi, meal })),
    );
    const allIngredientGroups = buildIngredientGroups(
      allPairs,
      foods,
      purchases,
    );
    groups.push({
      keyString: "__no-recipe__",
      description: "Direkt verzehren",
      meals: noDishMeals,
      steps: [
        {
          stepIndex: undefined,
          stepText: null,
          ingredientGroups: allIngredientGroups,
        },
      ],
      allIngredientGroups,
      isNoDishGroup: true,
    });
  }

  return groups;
}

export function buildSingleDishGroup(
  keyString: string,
  dishMeals: MealWithIntakes[],
  foods: FoodDto[],
  purchases: PurchaseDto[],
  loadedDishes: Record<number, DishModification>,
): DishGroup {
  const first = dishMeals[0].meal;
  const dishId = first.dishId;
  const loadedDish = dishId ? loadedDishes[dishId] : undefined;

  // Let's resolve missing stepIndex dynamically if null/undefined!
  if (loadedDish && loadedDish.steps?.length > 0) {
    dishMeals.forEach((meal) => {
      meal.meal.mealIngredients.forEach((mi) => {
        if (mi.stepIndex === undefined || mi.stepIndex === null) {
          const matchingStep = loadedDish.steps.find((s) =>
            s.ingredients.some(
              (si) =>
                si.foodUnitId === mi.foodUnitId ||
                si.alternatives?.some(
                  (alt) => alt.foodUnitId === mi.foodUnitId,
                ),
            ),
          );
          if (matchingStep) {
            mi.stepIndex = matchingStep.position ?? 0;
          }
        }
      });
    });
  }

  const allPairs: IngredientPair[] = dishMeals.flatMap((meal) =>
    meal.meal.mealIngredients.map((mi) => ({ mi, meal })),
  );

  const steps: CookingStep[] = [];

  if (loadedDish && loadedDish.steps?.length > 0) {
    loadedDish.steps.forEach((s) => {
      const position = s.position ?? 0;
      const pairsForStep = allPairs.filter((p) => p.mi.stepIndex === position);

      s.ingredients.forEach((si) => {
        dishMeals.forEach((meal) => {
          const personId = meal.meal.personId;

          const hasAnyOptionPlanned = pairsForStep.some((p) => {
            return (
              p.meal.meal.personId === personId &&
              (p.mi.foodUnitId === si.foodUnitId ||
                si.alternatives?.some(
                  (alt) => alt.foodUnitId === p.mi.foodUnitId,
                ))
            );
          });

          if (!hasAnyOptionPlanned) {
            const choice = si.personChoices?.find(
              (pc) => pc.personId === personId,
            );
            const preferredUnitId = choice ? choice.foodUnitId : si.foodUnitId;
            const foodUnit = foodByUnit(foods, preferredUnitId!);
            const syntheticMi: MealIngredient = {
              id: -1,
              amount: 0,
              foodUnitDescription:
                foodUnit?.foodUnits?.find(
                  (fu: any) => fu.id === preferredUnitId,
                )?.description || "",
              foodUnitFoodDescription: foodUnit?.description || "",
              foodUnitFoodId: foodUnit?.id || -1,
              foodUnitId: preferredUnitId || -1,
              mealId: meal.meal.id,
              purchaseUnitDescription: "",
              purchaseUnitId: -1,
              purchaseUnitPurchaseDescription: "",
              purchaseUnitPurchaseId: -1,
              stepIndex: position,
              stepText: s.text,
            };
            const pair = { mi: syntheticMi, meal };
            pairsForStep.push(pair);
            allPairs.push(pair);
          }
        });
      });

      const ingredientGroups = buildIngredientGroups(
        pairsForStep,
        foods,
        purchases,
        s.ingredients,
      );

      steps.push({
        stepIndex: position,
        stepText: s.text,
        ingredientGroups,
      });
    });
  } else {
    const stepOrder: string[] = [];
    const stepMap = new Map<
      string,
      {
        si: number | undefined;
        text: string | null | undefined;
        pairs: IngredientPair[];
      }
    >();
    for (const pair of allPairs) {
      const si = pair.mi.stepIndex ?? undefined;
      const sk = si !== undefined ? String(si) : "__";
      if (!stepMap.has(sk)) {
        stepOrder.push(sk);
        stepMap.set(sk, { si, text: pair.mi.stepText, pairs: [] });
      }
      stepMap.get(sk)!.pairs.push(pair);
    }
    const sortedKeys = [...stepOrder].sort((a, b) => {
      if (a === "__" && b === "__") return 0;
      if (a === "__") return 1;
      if (b === "__") return -1;
      return Number(a) - Number(b);
    });

    sortedKeys.forEach((sk) => {
      const s = stepMap.get(sk)!;
      steps.push({
        stepIndex: s.si,
        stepText: s.text,
        ingredientGroups: buildIngredientGroups(s.pairs, foods, purchases),
      });
    });
  }

  const allIngredientGroups = buildIngredientGroups(
    allPairs,
    foods,
    purchases,
    loadedDish?.steps?.flatMap((s) => s.ingredients),
  );

  return {
    keyString,
    description:
      first.dishDescription ||
      first.foodDescription ||
      first.purchaseDescription ||
      "",
    meals: dishMeals,
    steps,
    allIngredientGroups,
  };
}

export function getFoodIdForGroup(
  group: IngredientGroup,
  purchases: PurchaseDto[],
  foods: FoodDto[],
  activeUnitId?: number,
): number | undefined {
  if (group.isFoodBased) {
    const originalFoodId = group.pairs[0]?.mi.foodUnitFoodId;
    const activeFoodUnitId = activeUnitId || group.pairs[0]?.mi.foodUnitId;
    const foodId = foodByUnit(foods, activeFoodUnitId!)?.id || originalFoodId;
    return !foodId || foodId === -1 ? undefined : foodId;
  } else {
    const purchaseId = group.pairs[0]?.mi.purchaseUnitPurchaseId;
    if (!purchaseId || purchaseId === -1) return undefined;
    const foodId = findById(purchases, purchaseId)?.foodId;
    return !foodId || foodId === -1 ? undefined : foodId;
  }
}

/** Does this ingredient have more products than just the current one? */
export function hasSelectableProducts(
  group: IngredientGroup,
  purchases: PurchaseDto[],
  foods: FoodDto[],
  activeUnitId?: number,
): boolean {
  const foodId = getFoodIdForGroup(group, purchases, foods, activeUnitId);
  if (!foodId) return false;
  const relevantPurchases = purchases.filter(
    (p) => p.foodId === foodId && p.purchaseRelevant,
  );
  return relevantPurchases.length > 1;
}

export function getRelevantPurchases(
  group: IngredientGroup,
  purchases: PurchaseDto[],
  foods: FoodDto[],
  activeUnitId?: number,
): Purchase[] {
  const foodId = getFoodIdForGroup(group, purchases, foods, activeUnitId);
  if (!foodId) return [];
  return purchases.filter((p) => p.foodId === foodId && p.purchaseRelevant);
}

export function computePersonNutri(
  meals: MealWithIntakes[],
  personId: number,
  purchases: PurchaseDto[],
  foods: FoodDto[],
  overrides: CookingOverrides,
): NutriValues {
  const allIntakes: any[] = [];
  meals.forEach((m) => {
    const res = createCustomIntakes(m, purchases, foods, overrides);
    allIntakes.push(...res.intakes);
  });

  return allIntakes
    .filter((i) => i.personId === personId)
    .reduce(
      (acc, intake) => {
        const n = nutriValuesFromIntake(intake, purchases);
        return {
          kcal: acc.kcal + n.kcal,
          fat: acc.fat + n.fat,
          carbs: acc.carbs + n.carbs,
          protein: acc.protein + n.protein,
        };
      },
      { ...emptyNutriValue },
    );
}
